<template>
  <div ref="canvasContainerRef" aria-hidden="true">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, reactive, watch, computed } from "vue";
import useMousePosition from "./../utils/MousePosition";

export default {
  name: "Particles",
  props: {
    quantity: {
      type: Number,
      default: 35,
    },
    staticity: {
      type: Number,
      default: 50,
    },
    ease: {
      type: Number,
      default: 50,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const canvasRef = ref(null);
    const canvasContainerRef = ref(null);
    const context = ref(null);
    const circles = ref([]);
    const mousePosition = useMousePosition();
    const mouse = reactive({ x: 0, y: 0 });
    const canvasSize = reactive({ w: 0, h: 0 });
    const dpr = ref(1);
    const colorMode = useColorMode();
    const animationFrame = ref(null);

    onMounted(() => {
      dpr.value = window.devicePixelRatio || 1;
      if (canvasRef.value) {
        context.value = canvasRef.value.getContext("2d");
      }
      initCanvas();
      animate();
      if (!("ontouchstart" in window || navigator.maxTouchPoints)) {
        window.addEventListener("resize", initCanvas);
      }
    });

    onBeforeUnmount(() => {
      if (!("ontouchstart" in window || navigator.maxTouchPoints)) {
        window.removeEventListener("resize", initCanvas);
      }
      if (animationFrame.value) {
        window.cancelAnimationFrame(animationFrame.value);
      }
    });

    watch(
      () => mousePosition.value,
      () => {
        onMouseMove();
      }
    );

    watch(
      () => props.refresh,
      () => {
        initCanvas();
      }
    );
    const canvasWidth = computed(() =>
      canvasContainerRef.value ? canvasContainerRef.value.offsetWidth : "0"
    );

    const canvasHeight = computed(() =>
      canvasContainerRef.value ? canvasContainerRef.value.offsetHeight : "0"
    );

    const initCanvas = () => {
      resizeCanvas();
      drawParticles();
    };

    const onMouseMove = () => {
      if (canvasRef.value) {
        const rect = canvasRef.value.getBoundingClientRect();
        const { w, h } = canvasSize;
        const x = mousePosition.value.x - rect.left - w / 2;
        const y = mousePosition.value.y - rect.top - h / 2;
        const inside = x < w / 2 && x > -w / 2 && y < h / 2 && y > -h / 2;
        if (inside) {
          mouse.x = x;
          mouse.y = y;
        }
      }
    };

    const resizeCanvas = () => {
      if (canvasContainerRef.value && canvasRef.value && context.value) {
        circles.value.length = 0;
        canvasSize.w = canvasWidth.value;
        canvasSize.h = canvasHeight.value;
        canvasRef.value.width = canvasSize.w * dpr.value;
        canvasRef.value.height = canvasSize.h * dpr.value;
        canvasRef.value.style.width = canvasSize.w + "px";
        canvasRef.value.style.height = canvasSize.h + "px";
        context.value.scale(dpr.value, dpr.value);
      }
    };

    const circleParams = () => {
      const x = Math.floor(Math.random() * canvasSize.w);
      const y = Math.floor(Math.random() * canvasSize.h);
      const translateX = 0;
      const translateY = 0;
      const size = Math.floor(Math.random() * 2) + 1;
      const alpha = 0;
      const targetAlpha = parseFloat((Math.random() * 0.6 + 0.1).toFixed(1));
      const dx = (Math.random() - 0.5) * 0.2;
      const dy = (Math.random() - 0.5) * 0.2;
      const magnetism = 0.1 + Math.random() * 4;
      return {
        x,
        y,
        translateX,
        translateY,
        size,
        alpha,
        targetAlpha,
        dx,
        dy,
        magnetism,
      };
    };

    const drawCircle = (circle, update = false) => {
      if (context.value) {
        const { x, y, translateX, translateY, size, alpha } = circle;
        context.value.translate(translateX, translateY);
        context.value.beginPath();
        context.value.arc(x, y, size, 0, 2 * Math.PI);
        // if (colorMode.preference == 'dark') {
        context.value.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        // } else {
        //   context.value.fillStyle = `rgba(0, 0, 128, ${alpha})`;
        //   // navy blue
        // }
        context.value.fill();
        context.value.setTransform(dpr.value, 0, 0, dpr.value, 0, 0);

        if (!update) {
          circles.value.push(circle);
        }
      }
    };

    const clearContext = () => {
      if (context.value) {
        context.value.clearRect(0, 0, canvasSize.w, canvasSize.h);
      }
    };

    const drawParticles = () => {
      clearContext();
      const particleCount = props.quantity;
      for (let i = 0; i < particleCount; i++) {
        const circle = circleParams();
        drawCircle(circle);
      }
    };

    const remapValue = (value, start1, end1, start2, end2) => {
      const remapped = ((value - start1) * (end2 - start2)) / (end1 - start1) + start2;
      return remapped > 0 ? remapped : 0;
    };

    const animate = () => {
      clearContext();
      circles.value.forEach((circle, i) => {
        // Handle the alpha value
        const edge = [
          circle.x + circle.translateX - circle.size, // distance from left edge
          canvasSize.w - circle.x - circle.translateX - circle.size, // distance from right edge
          circle.y + circle.translateY - circle.size, // distance from top edge
          canvasSize.h - circle.y - circle.translateY - circle.size, // distance from bottom edge
        ];
        const closestEdge = edge.reduce((a, b) => Math.min(a, b));
        const remapClosestEdge = parseFloat(
          remapValue(closestEdge, 0, 20, 0, 1).toFixed(2)
        );
        if (remapClosestEdge > 1) {
          circle.alpha += 0.02;
          if (circle.alpha > circle.targetAlpha) circle.alpha = circle.targetAlpha;
        } else {
          circle.alpha = circle.targetAlpha * remapClosestEdge;
        }
        circle.x += circle.dx;
        circle.y += circle.dy;
        circle.translateX +=
          (mouse.x / (props.staticity / circle.magnetism) - circle.translateX) /
          props.ease;
        circle.translateY +=
          (mouse.y / (props.staticity / circle.magnetism) - circle.translateY) /
          props.ease;
        // circle gets out of the canvas
        if (
          circle.x < -circle.size ||
          circle.x > canvasSize.w + circle.size ||
          circle.y < -circle.size ||
          circle.y > canvasSize.h + circle.size
        ) {
          // remove the circle from the array
          circles.value.splice(i, 1);
          // create a new circle
          const newCircle = circleParams();
          drawCircle(newCircle);
          // update the circle position
        } else {
          drawCircle(
            {
              ...circle,
              x: circle.x,
              y: circle.y,
              translateX: circle.translateX,
              translateY: circle.translateY,
              alpha: circle.alpha,
            },
            true
          );
        }
      });
      animationFrame.value = window.requestAnimationFrame(animate);
    };

    return {
      canvasRef,
      canvasContainerRef,
    };
  },
};
</script>

<style scoped>
@media (max-width: 639px) {
  div[aria-hidden="true"] {
    display: none !important;
  }
}
</style>
